/**
 * Safe-area мост embed ↔ native (по образцу push-token моста).
 *
 * В cross-origin iframe Capacitor WKWebView env(safe-area-inset-*) приходит
 * нулями — реальные инсеты знает только локальный нативный бандл
 * (mobile/client-app/src/safe-area.ts). Запрашиваем их у родителя:
 *
 *   embed → parent: { type: 'domcrm:get-safe-area' }
 *   parent → embed: { type: 'domcrm:safe-area', payload: { top, bottom, left, right } }
 *
 * Вне iframe (обычный браузер) локальный env() работает и мост не нужен.
 */

import { useEffect, useState } from 'react';

export type SafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

export const ZERO_INSETS: SafeAreaInsets = { top: 0, bottom: 0, left: 0, right: 0 };

/** Фолбэк для iPhone (Dynamic Island), если framed + widget_channel=mobile, а родитель молчит (старый бандл без моста). */
const FRAMED_FALLBACK_INSETS: SafeAreaInsets = { top: 44, bottom: 34, left: 0, right: 0 };

const PARENT_REPLY_TIMEOUT_MS = 800;

/** true — iOS (iPhone/iPad/iPod) по User-Agent. Фолбэк Dynamic Island применим только там. */
export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

function toInsetPx(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
}

/** Нормализует payload родителя; битый payload → null. */
export function normalizeSafeAreaPayload(payload: unknown): SafeAreaInsets | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  if (p.top === undefined && p.bottom === undefined && p.left === undefined && p.right === undefined) {
    return null;
  }
  return {
    top: toInsetPx(p.top),
    bottom: toInsetPx(p.bottom),
    left: toInsetPx(p.left),
    right: toInsetPx(p.right),
  };
}

/** Локальный env() через probe-div: работает в обычном браузере; в сломанном iframe даёт нули. */
export function readLocalEnvInsets(): SafeAreaInsets {
  if (typeof document === 'undefined') return { ...ZERO_INSETS };
  try {
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
      'padding-top:env(safe-area-inset-top, 0px);' +
      'padding-bottom:env(safe-area-inset-bottom, 0px);' +
      'padding-left:env(safe-area-inset-left, 0px);' +
      'padding-right:env(safe-area-inset-right, 0px);';
    document.body.appendChild(probe);
    try {
      const cs = window.getComputedStyle(probe);
      return {
        top: toInsetPx(cs.paddingTop),
        bottom: toInsetPx(cs.paddingBottom),
        left: toInsetPx(cs.paddingLeft),
        right: toInsetPx(cs.paddingRight),
      };
    } finally {
      probe.remove();
    }
  } catch {
    return { ...ZERO_INSETS };
  }
}

function isMobileChannelFrame(): boolean {
  try {
    if (!window.parent || window.parent === window) return false;
    return new URLSearchParams(window.location.search).get('widget_channel') === 'mobile';
  } catch {
    return false;
  }
}

/**
 * Просит инсеты у нативного родителя (mobile/client-app App.tsx).
 * null — мы не в mobile-iframe или ответ не пришёл за timeoutMs.
 */
export function requestParentSafeArea(timeoutMs: number = PARENT_REPLY_TIMEOUT_MS): Promise<SafeAreaInsets | null> {
  return new Promise((resolve) => {
    if (!isMobileChannelFrame()) {
      resolve(null);
      return;
    }
    let settled = false;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; payload?: unknown } | undefined;
      if (data?.type !== 'domcrm:safe-area') return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve(normalizeSafeAreaPayload(data.payload));
    };
    window.addEventListener('message', onMessage);
    try {
      window.parent.postMessage({ type: 'domcrm:get-safe-area' }, '*');
    } catch {
      /* ignore */
    }
    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, timeoutMs);
  });
}

const INITIAL_INSETS: SafeAreaInsets = readLocalEnvInsets();

/**
 * Инсеты для шапок/таб-баров мобильных шеллов:
 * локальный env() → ответ нативного родителя → iPhone-фолбэк при нулях (только iOS;
 * на Android фолбэк Dynamic Island не применяем — иначе лишние отступы на старом бандле).
 */
export function useSafeAreaInsets(): SafeAreaInsets {
  const [insets, setInsets] = useState<SafeAreaInsets>(INITIAL_INSETS);

  useEffect(() => {
    if (!isMobileChannelFrame()) return;
    let alive = true;
    void requestParentSafeArea().then((fromParent) => {
      if (!alive) return;
      if (fromParent) {
        setInsets(fromParent);
        return;
      }
      setInsets((prev) => {
        if (prev.top !== 0 || prev.bottom !== 0) return prev;
        return isIOSDevice() ? { ...FRAMED_FALLBACK_INSETS } : { ...ZERO_INSETS };
      });
    });
    return () => {
      alive = false;
    };
  }, []);

  return insets;
}
